import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ExternalLink, CheckCircle2, XCircle } from "lucide-react";
import { fmtDate, fmtDateTime } from "@/components/bp/bpLabels";
import { downloadAuth } from "@/lib/downloadAuth";
import { VigBadge, vigSentence } from "@/components/bp/vigilanceLabels";

/** Fiche admin BP — attestation de vigilance URSSAF : état, document, validation / refus, historique. */
export const BpVigilanceReview = ({ bp, onChanged }) => {
  const [v, setV] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(`/admin/bp/${bp.id}/vigilance`).then((r) => setV(r.data)).catch(() => setV({ error: true }));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bp.id]);

  const decide = async (approve) => {
    const reason = approve ? "" : window.prompt("Motif du refus (transmis au partenaire) :", "");
    if (!approve && !reason) return;
    setBusy(true);
    try { await api.post(`/admin/bp/${bp.id}/vigilance/${v.doc_id}/${approve ? "approve" : "reject"}`, approve ? {} : { reason }); toast.success(approve ? "Attestation validée — versements débloqués" : "Attestation refusée — partenaire prévenu"); await load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const open = (d) => downloadAuth(`/admin/bp/${bp.id}/vigilance/${d.id}/file`, d.filename, { open: true }).catch(() => toast.error("Document indisponible"));

  if (!v || v.error) return null;
  return (
    <div className={`border p-3 space-y-2 text-xs ${v.ok ? "border-[#A7F3D0] bg-[#ECFDF5]" : v.state === "pending" ? "border-[#FFD700] bg-[#FFFBEB]" : "border-[#FECACA] bg-[#FEF2F2]"}`} data-testid="bp-vig-review">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldCheck className="h-4 w-4" /><span className="text-[10px] uppercase tracking-[0.25em] font-bold">Attestation de vigilance URSSAF</span>
        <VigBadge state={v.state} testid="bp-vig-state" />
        {v.doc_id && <button type="button" onClick={() => open({ id: v.doc_id, filename: v.filename })} className="ml-auto inline-flex items-center gap-1 text-[#002FA7] font-bold hover:underline" data-testid="bp-vig-open"><ExternalLink className="h-3 w-3" />Voir le document</button>}
      </div>
      <div data-testid="bp-vig-sentence">{vigSentence(v)}</div>
      {v.state === "pending" && (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => decide(true)} disabled={busy} className="h-7 text-[10px] uppercase bg-[#059669] text-white" data-testid="bp-vig-approve"><CheckCircle2 className="h-3 w-3 mr-1" />Valider (6 mois)</Button>
          <Button size="sm" variant="outline" onClick={() => decide(false)} disabled={busy} className="h-7 text-[10px] uppercase text-[#E60000]" data-testid="bp-vig-reject"><XCircle className="h-3 w-3 mr-1" />Refuser</Button>
          <span className="text-[10px] text-[#868E96] self-center">Vérifiez le code de sécurité du document sur urssaf.fr avant de valider.</span>
        </div>
      )}
      {(v.history || []).length > 1 && (
        <details className="text-[10px]"><summary className="cursor-pointer text-[#495057]">Historique ({v.history.length})</summary>
          <div className="mt-1 space-y-0.5">{v.history.map((d) => <div key={d.id} className="flex items-center gap-2" data-testid={`bp-vig-doc-${d.id}`}><span>{fmtDateTime(d.uploaded_at)} · délivrée le {fmtDate(d.issued_at)} · jusqu'au {fmtDate(d.expires_at)}</span><VigBadge state={d.status} /><button type="button" onClick={() => open(d)} className="text-[#002FA7] hover:underline">voir</button></div>)}</div>
        </details>
      )}
    </div>
  );
};
